import React, { useState, useEffect } from 'react';
import { Plus, Download, Eye, Edit, Filter, CreditCard, Search } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { StatusBadge } from '../ui/StatusBadge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { paymentService } from '../../lib/database/payments';
import { CreatePaymentModal } from './CreatePaymentModal';
import { EditPaymentModal } from './EditPaymentModal';
import { ViewPaymentModal } from './ViewPaymentModal';

export const PaymentsList: React.FC = () => {
  const [payments, setPayments] = useState<any[]>([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const { data } = await paymentService.getPayments();
      if (data) {
        setPayments(data);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const handleCreateSuccess = () => {
    fetchPayments();
  };

  const handleEditSuccess = () => {
    fetchPayments();
    setSelectedPayment(null);
  };

  const handleViewPayment = (payment: any) => {
    setSelectedPayment(payment);
    setShowViewModal(true);
  };

  const handleEditPayment = (payment: any) => {
    setSelectedPayment(payment);
    setShowEditModal(true);
  };

  const handleEditFromView = () => {
    setShowViewModal(false);
    setShowEditModal(true);
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'cash':
        return 'bg-success-100 text-success-700 border-success-200';
      case 'card':
        return 'bg-primary-100 text-primary-700 border-primary-200';
      case 'bank_transfer':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'upi':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'cheque':
        return 'bg-warning-100 text-warning-700 border-warning-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.invoice?.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.invoice?.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.reference_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filter === 'all' || payment.method === filter;
    
    return matchesSearch && matchesFilter;
  });

  const totalPayments = payments.reduce((sum, payment) => sum + Number(payment.amount), 0);
  const paymentMethods = ['cash', 'card', 'bank_transfer', 'upi', 'cheque'];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="bg-white p-6 rounded-xl h-96"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payments</h1>
          <p className="text-gray-600 mt-2">Track and manage all payment transactions</p>
        </div>
        <Button 
          icon={Plus} 
          className="shadow-lg bg-gradient-to-r from-success-600 to-success-700 hover:from-success-700 hover:to-success-800"
          onClick={() => setShowCreateModal(true)}
        >
          Record Payment
        </Button>
      </div>

      {/* Search and Filters */}
      <Card padding="sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search payments, invoices, or clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80 text-sm"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Method:</span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  filter === 'all'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                All
              </button>
              {paymentMethods.map((method) => (
                <button
                  key={method}
                  onClick={() => setFilter(method)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 capitalize ${
                    filter === method
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {method.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Enhanced Payments Table */}
      <Card padding="none" className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Payment Details</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Invoice</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Client</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Amount</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Method</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Date</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-500 bg-gray-50">
                    {searchTerm || filter !== 'all' ? 'No payments found matching your criteria.' : 'No payments recorded yet. Record your first payment!'}
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment, index) => (
                  <tr
                    key={payment.id}
                    className={`transition-all duration-200 hover:bg-green-50 hover:shadow-sm ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                    }`}
                  >
                    <td className="py-4 px-6 border-l-4 border-transparent hover:border-success-400">
                      <div>
                        <p className="font-semibold text-gray-900">Payment #{payment.id.slice(-8)}</p>
                        {payment.reference_id && (
                          <p className="text-sm text-gray-500">Ref: {payment.reference_id}</p>
                        )}
                        <p className="text-sm text-gray-500">
                          Recorded {formatDate(payment.created_at)}
                        </p>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div>
                        <p className="font-medium text-gray-900">{payment.invoice?.invoice_number || 'N/A'}</p>
                        <p className="text-sm text-gray-500">
                          Total: {formatCurrency(Number(payment.invoice?.total || 0))}
                        </p>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div>
                        <p className="font-medium text-gray-900">{payment.invoice?.client?.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">{payment.invoice?.client?.company || 'No company'}</p>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <p className="font-bold text-lg text-success-600">
                        {formatCurrency(Number(payment.amount))}
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${getMethodColor(payment.method)}`}>
                        {payment.method.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-gray-900 font-medium">{formatDate(payment.payment_date)}</p>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          icon={Eye}
                          onClick={() => handleViewPayment(payment)}
                          className="hover:bg-blue-100"
                        >
                          View
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          icon={Edit}
                          onClick={() => handleEditPayment(payment)}
                          className="hover:bg-yellow-100"
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          icon={Download}
                          className="hover:bg-green-100"
                        >
                          Receipt
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card padding="sm" className="text-center bg-gradient-to-br from-success-50 to-success-100 border-success-200">
          <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <CreditCard className="w-6 h-6 text-success-600" />
          </div>
          <p className="text-2xl font-bold text-success-700">{formatCurrency(totalPayments)}</p>
          <p className="text-sm text-success-600">Total Received</p>
        </Card>
        <Card padding="sm" className="text-center bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <p className="text-2xl font-bold text-blue-700">{payments.length}</p>
          <p className="text-sm text-blue-600">Total Payments</p>
        </Card>
        <Card padding="sm" className="text-center bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
          <p className="text-2xl font-bold text-primary-700">
            {payments.filter(p => p.method === 'card').length}
          </p>
          <p className="text-sm text-primary-600">Card Payments</p>
        </Card>
        <Card padding="sm" className="text-center bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <p className="text-2xl font-bold text-purple-700">
            {payments.filter(p => p.method === 'upi').length}
          </p>
          <p className="text-sm text-purple-600">UPI Payments</p>
        </Card>
      </div>

      {/* Modals */}
      <CreatePaymentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      <EditPaymentModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedPayment(null);
        }}
        onSuccess={handleEditSuccess}
        payment={selectedPayment}
      />

      <ViewPaymentModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedPayment(null);
        }}
        onEdit={handleEditFromView}
        payment={selectedPayment}
      />
    </div>
  );
};